import { exerciseNow, setExerciseTimeout, clearExerciseTimeout } from "../exercises/pause-clock";
import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useState } from "react";
import type { Wordart, WordartenExerciseConfig, WordartenRunSettings } from "@calega/content";
import type { WordartenAssignment, WordartenRun } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { useRealtime } from "../realtime/RealtimeContext";
import { removeAssignment } from "./assignment-interactions";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { ExerciseDurationSelect } from "./ExerciseSettings";
import { abortExerciseRun, submitExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";

type Phase = "running" | "feedback" | "complete";
type AssignmentTool = Wordart | "remove";
type AssignmentByToken = Record<string, WordartenAssignment>;

interface Answer {
  sentenceId: string;
  correct: boolean;
}

interface State {
  runId: string;
  run: WordartenRun;
  index: number;
  phase: Phase;
  assignments: AssignmentByToken;
  selectedTokenId: string | null;
  selectedPos: AssignmentTool | null;
  endsAt: number | null;
  answers: Answer[];
  result: { score: number; maxScore: number } | null;
}

const categoryClass: Record<string, string> = {
  Artikel: "article", Nomen: "noun", Adjektiv: "adjective", Verb: "verb", Adverb: "adverb", Pronomen: "pronoun", Präposition: "preposition", Konjunktion: "conjunction", Interjektion: "interjection", Numerale: "numeral", Partikel: "particle",
};

export function formatWordartenResult(score: number, maxScore: number) {
  return `${score} von ${maxScore} Wörtern richtig`;
}

export function Wordarten({ account, config, pageId }: { account: Account; config: WordartenExerciseConfig; pageId: string }) {
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const syncFollower = Boolean(realtime.session && !facilitator);
  const canControl = !realtime.session || facilitator;
  const canInteract = !realtime.session || facilitator || syncFollower;
  const [mode, setMode] = useState<WordartenRunSettings["mode"]>(config.defaultRunMode);
  const [count, setCount] = useState(config.defaultCount);
  const [durationSeconds, setDurationSeconds] = useState(config.defaultDurationSeconds);
  const [categories, setCategories] = useState<Wordart[]>(config.defaultCategories);
  const [includeNominalizations, setIncludeNominalizations] = useState(config.defaultIncludeNominalizations);
  const [exercise, setExercise] = useState<State | null>(null);
  useLearningDisplayItem(exercise?.runId, exercise?.index ?? 0, Boolean(exercise && exercise.phase !== "complete"));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [, setClockTick] = useState(0);

  function publish(next: State) {
    realtime.publishExercise({ exerciseType: "wortarten", pageId, state: next });
  }

  function publishReset() {
    realtime.publishExercise({ exerciseType: "wortarten", pageId, state: null });
  }

  useEffect(() => {
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: State | null } | null;
    if (synced?.exerciseType === "wortarten" && synced.pageId === pageId && synced.state === null) {
      setExercise(null);
      return;
    }
    if (synced?.exerciseType === "wortarten" && synced.pageId === pageId && synced.state) setExercise(synced.state);
  }, [realtime.state.exercise, pageId]);

  useEffect(() => {
    if (!exercise?.endsAt || exercise.phase === "complete") return;
    const interval = window.setInterval(() => setClockTick((value) => value + 1), 250);
    return () => window.clearInterval(interval);
  }, [exercise?.endsAt, exercise?.phase]);

  useEffect(() => {
    if (busy || !exercise?.endsAt || exercise.phase === "complete" || !canControl) return;
    const remaining = Math.max(0, exercise.endsAt - exerciseNow(realtime.serverNow));
    const timer = setExerciseTimeout(() => finish().catch((error) => setMessage(error.message)), remaining + 20);
    return () => clearExerciseTimeout(timer);
  }, [exercise?.endsAt, exercise?.phase, exercise?.index, exercise?.answers.length, canControl, busy]);

  function currentSettings(): WordartenRunSettings {
    return { mode, count, durationSeconds, categories, includeNominalizations };
  }

  function setFromSettings(settings: WordartenRunSettings) {
    setMode(settings.mode);
    setCount(settings.count);
    setDurationSeconds(settings.durationSeconds);
    setCategories(settings.categories);
    setIncludeNominalizations(settings.includeNominalizations ?? false);
  }

  async function start(settings = currentSettings()) {
    if (busy || !canControl) return;
    setBusy(true);
    setMessage("Durchlauf wird vorbereitet …");
    try {
      const result = await startExerciseRun<{ runId: string; exercise: WordartenRun }>({ contentPageId: pageId, settings });
      if (!result.exercise.sentences.length) throw new Error("Für diese Wortartenauswahl sind keine passenden Sätze vorhanden.");
      const next: State = {
        runId: result.runId,
        run: result.exercise,
        index: 0,
        phase: "running",
        assignments: {},
        selectedTokenId: null,
        selectedPos: null,
        endsAt: result.exercise.settings.mode === "time" ? exerciseNow(realtime.serverNow) + result.exercise.settings.durationSeconds * 1_000 : null,
        answers: [],
        result: null,
      };
      setExercise(next);
      setMessage("");
      publish(next);
    } finally {
      setBusy(false);
    }
  }

  async function abortCurrentRun() {
    if (exercise && exercise.phase !== "complete") await abortExerciseRun(exercise.runId);
  }

  async function backToSettings() {
    if (!exercise || busy || !canControl) return;
    setBusy(true);
    try {
      await abortCurrentRun();
      setFromSettings(exercise.run.settings);
      setExercise(null);
      setMessage("");
      publishReset();
    } finally {
      setBusy(false);
    }
  }

  async function remix() {
    if (!exercise || busy || !canControl) return;
    const settings = exercise.run.settings;
    setBusy(true);
    try {
      await abortCurrentRun();
    } finally {
      setBusy(false);
    }
    await start(settings);
  }

  function assign(tokenId: string, chosenPos: Wordart) {
    if (!exercise || exercise.phase !== "running" || !canInteract) return;
    const existing = exercise.assignments[tokenId];
    if (existing?.chosenPos === chosenPos) return;
    const nextOrder = Math.max(0, ...Object.values(exercise.assignments).map(({ assignmentOrder }) => assignmentOrder ?? 0)) + 1;
    const assignment: WordartenAssignment = {
      tokenId,
      chosenPos,
      assignmentOrder: nextOrder,
      history: [...(existing?.history ?? []), chosenPos],
    };
    const next = { ...exercise, assignments: { ...exercise.assignments, [tokenId]: assignment }, selectedTokenId: null, selectedPos: null };
    setExercise(next);
    publish(next);
  }

  function clearAssignment(tokenId: string) {
    if (!exercise || exercise.phase !== "running" || !canInteract) return;
    const next = { ...exercise, assignments: removeAssignment(exercise.assignments, tokenId), selectedTokenId: null, selectedPos: null };
    setExercise(next);
    publish(next);
  }

  function selectToken(tokenId: string) {
    if (!exercise || exercise.phase !== "running" || !canInteract) return;
    if (exercise.selectedPos) {
      if (exercise.selectedPos === "remove") {
        clearAssignment(tokenId);
        return;
      }
      assign(tokenId, exercise.selectedPos);
      return;
    }
    const next = { ...exercise, selectedTokenId: exercise.selectedTokenId === tokenId ? null : tokenId };
    setExercise(next);
    publish(next);
  }

  function selectPos(pos: AssignmentTool) {
    if (!exercise || exercise.phase !== "running" || !canInteract) return;
    if (exercise.selectedTokenId) {
      if (pos === "remove") {
        clearAssignment(exercise.selectedTokenId);
        return;
      }
      assign(exercise.selectedTokenId, pos);
      return;
    }
    const next = { ...exercise, selectedPos: exercise.selectedPos === pos ? null : pos };
    setExercise(next);
    publish(next);
  }

  async function check() {
    if (!exercise || exercise.phase !== "running" || busy || !canInteract) return;
    const sentence = exercise.run.sentences[exercise.index];
    if (!sentence) return;
    setBusy(true);
    try {
      const assignments = sentence.tokens.map((token) => exercise.assignments[token.id] ?? { tokenId: token.id, chosenPos: null, assignmentOrder: null, history: [] });
      const correct = assignments.every((assignment) => assignment.chosenPos === sentence.tokens.find(({ id }) => id === assignment.tokenId)!.expectedPos);
      await submitExerciseAnswer(exercise.runId, { type: "wortarten", sentenceId: sentence.id, assignments });
      const next = { ...exercise, phase: "feedback" as const, answers: [...exercise.answers, { sentenceId: sentence.id, correct }] };
      setExercise(next);
      publish(next);
    } finally {
      setBusy(false);
    }
  }

  async function next() {
    if (!exercise || exercise.phase !== "feedback" || busy || !canControl) return;
    if ((exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.sentences.length) {
      await finish();
      return;
    }
    const next = { ...exercise, index: exercise.index + 1, phase: "running" as const, assignments: {}, selectedTokenId: null, selectedPos: null };
    setExercise(next);
    publish(next);
  }

  async function finish() {
    if (!exercise || exercise.phase === "complete" || busy || !canControl) return;
    await completion.complete(exercise.runId, (result) => {
      const next = { ...exercise, result, phase: "complete" as const };
      setExercise(next);
      publish(next);
      setMessage("");
    });
  }
  async function closeCompletedRun(repeat: boolean) { if (!exercise || exercise.phase !== "complete") return; if (repeat) await remix(); else await backToSettings(); }

  function toggleCategory(category: Wordart) {
    const definition = config.categories.find(({ id }) => id === category);
    if (definition?.base) return;
    setCategories((current) => current.includes(category) ? current.filter((id) => id !== category) : [...current, category]);
  }

  if (syncFollower && !exercise) return <ExerciseWaitingCard className="wordarten-exercise" />;

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (!exercise) {
    return <section className="wordarten-exercise">
      <p className="quiz-instructions learning-text">{config.instructions}</p>
      <div className="wordarten-settings">
        <fieldset><legend>Durchlauf</legend><div className="blitz-choice-row">
          <label className="checkbox-label"><input type="radio" name={`${pageId}-wortarten-modus`} checked={mode === "count"} onChange={() => setMode("count")} />Feste Anzahl</label>
          <label className="checkbox-label"><input type="radio" name={`${pageId}-wortarten-modus`} checked={mode === "time"} onChange={() => setMode("time")} />Auf Zeit</label>
        </div></fieldset>
        {mode === "count" ? <label>Anzahl Sätze<select value={count} onChange={(event) => setCount(Number(event.target.value))}>{[1, 2, 3, 5, 10, 15, 20, 30].map((value) => <option value={value} key={value}>{value}</option>)}</select></label> : <ExerciseDurationSelect value={durationSeconds} onChange={setDurationSeconds} />}
        {config.mode === "generator" && <fieldset><legend>Wortarten</legend><div className="blitz-choice-row">
          {config.categories.map((category) => <label className="checkbox-label" key={category.id}><input type="checkbox" disabled={category.base} checked={categories.includes(category.id)} onChange={() => toggleCategory(category.id)} />{category.label}</label>)}
          <label className="checkbox-label"><input type="checkbox" checked={includeNominalizations} onChange={(event) => setIncludeNominalizations(event.target.checked)} />Nominalisierungen</label>
        </div></fieldset>}
      </div>
      <button className="primary" disabled={busy || !canControl} onClick={() => start().catch((error) => setMessage(error.message))}>Wortarten starten</button>
      {message && <div className="alert info">{message}</div>}
    </section>;
  }

  const sentence = exercise.run.sentences[exercise.index];
  const timeRemaining = exercise.endsAt ? Math.max(0, Math.ceil((exercise.endsAt - exerciseNow(realtime.serverNow)) / 1_000)) : null;
  const currentSentenceCorrect = exercise.answers.find((answer) => answer.sentenceId === sentence?.id)?.correct;
  return <section className="wordarten-exercise">
    {canControl && exercise.phase !== "complete" && <ExerciseMenu>
      <button className="secondary small" disabled={busy} onClick={() => backToSettings().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button>
      <button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button>
    </ExerciseMenu>}
    <div className="exercise-progress">{exercise.run.settings.mode === "time" ? `${exercise.answers.length} Sätze beantwortet · ${timeRemaining ?? 0} s` : `Satz ${Math.min(exercise.index + 1, exercise.run.sentences.length)} von ${exercise.run.sentences.length}`}</div>
    {exercise.phase === "complete" ? <div className="reime-result"><strong>{exercise.result ? formatWordartenResult(exercise.result.score, exercise.result.maxScore) : formatWordartenResult(exercise.answers.filter((entry) => entry.correct).length, exercise.answers.length)}</strong><p>Der Durchlauf ist abgeschlossen.</p></div> : sentence && <>
      <div className="wordarten-card">
        <h2 className="learning-text">Ordne jedem Wort die passende Wortart zu.</h2>
        <div className="wordarten-sentence learning-text">{sentence.tokens.map((token, index) => {
          const assignment = exercise.assignments[token.id];
          const isCorrect = exercise.phase === "feedback" && assignment?.chosenPos === token.expectedPos;
          const isWrong = exercise.phase === "feedback" && !isCorrect;
          return <span key={token.id}><button className={`wordarten-token learning-text ${assignment?.chosenPos ? `assigned ${categoryClass[assignment.chosenPos]}` : ""} ${exercise.selectedTokenId === token.id ? "selected" : ""} ${isCorrect ? "is-correct" : ""} ${isWrong ? "is-wrong" : ""}`} disabled={exercise.phase !== "running" || busy || !canInteract} onClick={() => selectToken(token.id)}>{token.surface}</button>{token.after ?? (index + 1 < sentence.tokens.length ? " " : "")}</span>;
        })}{sentence.suffix}</div>
        <div className="wordarten-choice-grid">
          {exercise.run.settings.categories.map((category) => <button className={`secondary wordarten-choice learning-text ${categoryClass[category]} ${exercise.selectedPos === category ? "selected" : ""}`} aria-pressed={exercise.selectedPos === category} disabled={exercise.phase !== "running" || busy || !canInteract} onClick={() => selectPos(category)} key={category}>{category}</button>)}
          <button className={`secondary wordarten-choice assignment-delete ${exercise.selectedPos === "remove" ? "selected" : ""}`} aria-pressed={exercise.selectedPos === "remove"} disabled={exercise.phase !== "running" || busy || !canInteract} onClick={() => selectPos("remove")}>× Zuordnung löschen</button>
        </div>
      </div>
      {exercise.phase === "feedback" && <div className={`answer-feedback learning-text ${currentSentenceCorrect ? "correct" : "wrong"}`}>{currentSentenceCorrect ? "Richtig zugeordnet." : "Noch nicht ganz. Die richtigen Wortarten sind jetzt grün markiert."}</div>}
      {exercise.phase === "feedback" ? canControl && <div className="reime-actions"><button className="primary" disabled={busy} onClick={() => next().catch((error) => setMessage(error.message))}>{(exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.sentences.length ? "Auswertung ansehen" : "Nächster Satz"}</button></div> : <div className="reime-actions"><button className="primary" disabled={busy} onClick={() => check().catch((error) => setMessage(error.message))}>Überprüfen</button></div>}
    </>}
    {canControl && exercise.phase === "complete" && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}
    {exercise.phase === "complete" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}
    {message && <div className="alert info">{message}</div>}
  </section>;
}
